import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Receipt as FeeLedgerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import { useLedgerLookups } from '../Journals';
import { StatStrip, fmtDate, getErrorMessage, unwrap } from './ArShared';

const studentLabel = (acc, look) => {
  if (acc?.customer) return acc.customer.name || `Customer ${acc.customer_id}`;
  if (acc?.name) return acc.name;
  return look.customerLabel(acc?.customer_id ?? acc?.id, `Customer ${acc?.customer_id ?? acc?.id ?? ''}`);
};

const accountBalance = (acc) => Number(
  acc?.balance ?? acc?.current_balance ?? acc?.outstanding_balance ?? acc?.balance_due ?? 0
);

const extRef = (acc) => acc?.ext_ref ?? acc?.customer?.ext_ref ?? '-';

const emptyStatement = () => ({
  open: false, customerId: null, label: '', start_date: '', end_date: '',
  loading: false, rows: [], opening: 0, closing: 0,
});

// Per-student AR subledger: one running-balance account per synced
// customer (customer_id = schoolsync students.id - see
// SchoolAccounting::syncStudent). Read-only here - accounts are created by
// the sync bridge, not by hand. Each row links out to the existing
// fee-collection ledger for the same student, so accountants can cross-
// check the AR statement against fee collection history in one click.
const CustomerLedgerTab = () => {
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.LEDGER.RECEIVABLES, ACTIONS.READ);
  const look = useLedgerLookups();
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();

  const [aging, setAging] = useState(null);
  const [statement, setStatement] = useState(emptyStatement());

  useEffect(() => {
    if (!canRead) return;
    let alive = true;
    ledgerAPI.getDebtorAging()
      .then((res) => { if (alive) setAging(unwrap(res)?.totals ?? unwrap(res) ?? null); })
      .catch(() => { if (alive) setAging(null); });
    return () => { alive = false; };
  }, [canRead]);

  const loadStatement = async (customerId, label, extra = {}) => {
    try {
      setStatement((p) => ({ ...p, loading: true }));
      const params = {
        customer_id: customerId,
        ...(extra.start_date ?? statement.start_date ? { start_date: extra.start_date ?? statement.start_date } : {}),
        ...(extra.end_date ?? statement.end_date ? { end_date: extra.end_date ?? statement.end_date } : {}),
      };
      const res = await ledgerAPI.getSubsidiaryLedgerEntries(params);
      const payload = unwrap(res);
      const rows = Array.isArray(payload) ? payload : (payload.entries ?? payload.transactions ?? payload.items ?? []);
      setStatement((p) => ({
        ...p,
        rows,
        opening: Number(payload.opening_balance) || 0,
        closing: Number(payload.closing_balance ?? payload.balance) || 0,
        loading: false,
      }));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch statement'), 'error');
      setStatement((p) => ({ ...p, loading: false }));
    }
  };

  const openStatement = (acc) => {
    const customerId = acc.customer_id ?? acc.id;
    setStatement({ ...emptyStatement(), open: true, customerId, label: studentLabel(acc, look) });
    loadStatement(customerId, studentLabel(acc, look));
  };

  const agingTiles = aging
    ? [
        { label: 'Current', value: formatAccountingAmount(aging.current) },
        { label: '1-30 days', value: formatAccountingAmount(aging.days_1_30), color: 'warning.main' },
        { label: '31-60 days', value: formatAccountingAmount(aging.days_31_60), color: 'warning.main' },
        { label: '61-90 days', value: formatAccountingAmount(aging.days_61_90), color: 'error.main' },
        { label: '90+ days', value: formatAccountingAmount(aging.over_90), color: 'error.main' },
        { label: 'Total Receivable', value: formatAccountingAmount(aging.total), color: 'primary.main' },
      ]
    : [];

  return (
    <>
      {aging && <StatStrip tiles={agingTiles} />}

      <LedgerListPage
        resource={RESOURCES.LEDGER.RECEIVABLES}
        title="Student Accounts"
        subtitle="Per-student AR balance, synced from fee billing and collection"
        idKey="customer_id"
        searchPlaceholder="Search student name, reg no..."
        emptyMessage="No student accounts found. Accounts appear here once a student is billed or pays a fee."
        fetchItems={(params) => ledgerAPI.getCustomerLedgerAccounts(params)}
        filters={[
          { type: 'numberrange', keyFrom: 'min_balance', keyTo: 'max_balance', label: 'balance' },
        ]}
        columns={[
          { label: 'Student', render: (a) => studentLabel(a, look) },
          { label: 'Reg. No.', render: (a) => extRef(a), hideOnMobile: true },
          { label: 'Balance', render: (a) => formatAccountingAmount(accountBalance(a)), align: 'right' },
        ]}
        rowActions={(a) => [
          { icon: FeeLedgerIcon, title: 'View statement', color: 'primary', onClick: () => openStatement(a) },
        ]}
      />

      <Dialog open={statement.open} onClose={() => setStatement(emptyStatement())} maxWidth="lg" fullWidth>
        <DialogTitle>Statement - {statement.label}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1} mb={2} alignItems="center">
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="From" value={statement.start_date} onChange={(e) => setStatement((p) => ({ ...p, start_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="To" value={statement.end_date} onChange={(e) => setStatement((p) => ({ ...p, end_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Button size="small" variant="outlined" onClick={() => loadStatement(statement.customerId, statement.label)}>Reload</Button>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Button
                size="small" variant="text" startIcon={<FeeLedgerIcon />}
                onClick={() => navigate(`/students/ledger/${statement.customerId}`)}
              >
                Open fee ledger
              </Button>
            </Grid>
          </Grid>
          {statement.loading ? (
            <LinearProgress />
          ) : statement.rows.length === 0 ? (
            <Typography color="text.secondary">No posted entries for this student in the selected period.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5}><em>Opening balance</em></TableCell>
                  <TableCell align="right"><strong>{formatAccountingAmount(statement.opening)}</strong></TableCell>
                </TableRow>
                {statement.rows.map((r, i) => (
                  <TableRow key={r.entry_id ?? i} hover>
                    <TableCell>{fmtDate(r.entry_date ?? r.transaction_date ?? r.date)}</TableCell>
                    <TableCell>{r.reference || r.invoice_number || r.payment_number || '-'}</TableCell>
                    <TableCell>{r.description || '-'}</TableCell>
                    <TableCell align="right">{Number(r.debit_amount ?? r.debit) > 0 ? formatAccountingAmount(r.debit_amount ?? r.debit) : '-'}</TableCell>
                    <TableCell align="right">{Number(r.credit_amount ?? r.credit) > 0 ? formatAccountingAmount(r.credit_amount ?? r.credit) : '-'}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(r.running_balance ?? r.balance ?? 0)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={5} align="right"><strong>Closing balance</strong></TableCell>
                  <TableCell align="right"><strong>{formatAccountingAmount(statement.closing)}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatement(emptyStatement())}>Close</Button>
        </DialogActions>
      </Dialog>

      {SnackbarComponent}
    </>
  );
};

export default CustomerLedgerTab;
